import { useState, type ReactNode } from "react";
import { Icon } from "@/components/icons";
import { Button } from "@/components/button";
import { StatCard } from "@/components/stat-card";
import { formatPercent } from "@/app/(app)/_lib/format";
import { ENTRY_FILTERS, ENTRY_FILTER_LABELS, type EntryFilter } from "./monitoring-filters";

export type ProcessingBadgeStatus = "creating" | "processing" | "idle";

type MonitoringHeaderProps = {
  total?: number;
  matched?: number;
  matchRate?: number;
  /** KPI values not fetched yet — the stat cards shimmer instead of showing 0. */
  kpiLoading?: boolean;
  /** Omitted while the account-wide state isn't known yet — no badge then. */
  status?: ProcessingBadgeStatus;
  dim?: boolean;
  showUploadBatch?: boolean;
  showDownloadReport?: boolean;
  canManageEntries?: boolean;
  onNewMonitoring?: () => void;
  onUploadBatch?: () => void;
  onDownloadReport?: () => void;
};

export function MonitoringHeader({
  total = 0,
  matched = 0,
  matchRate = 0,
  kpiLoading = false,
  status,
  dim = false,
  showUploadBatch = false,
  showDownloadReport = false,
  canManageEntries = true,
  onNewMonitoring,
  onUploadBatch,
  onDownloadReport,
}: MonitoringHeaderProps) {
  return (
    <div className="@container flex flex-col gap-2">
      <div className="flex flex-col gap-2 @sm:flex-row @sm:items-center @sm:justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-h4 font-semibold text-black">Monitoring</h1>
          {!dim && status && <ProcessingStatusBadge status={status} />}
        </div>
        <div className="flex items-center gap-1">
          {showDownloadReport && canManageEntries && (
            <Button
              hierarchy="secondary"
              size="sm"
              leftIcon={<Icon name="download" size={20} weight={300} />}
              onClick={onDownloadReport}
            >
              Download report
            </Button>
          )}
          {showUploadBatch && canManageEntries && (
            <Button
              hierarchy="secondary"
              size="sm"
              leftIcon={<Icon name="publish" size={20} weight={300} />}
              onClick={onUploadBatch}
            >
              Upload batch file
            </Button>
          )}
          {canManageEntries && (
            <Button
              hierarchy="tertiary"
              size="sm"
              leftIcon={<Icon name="add" size={20} weight={300} />}
              onClick={onNewMonitoring}
            >
              Add Entry
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 @lg:grid-cols-3">
        <StatCard
          icon="radar"
          label="Monitoring entries"
          value={dim ? "0" : total.toLocaleString()}
          dim={dim}
          loading={!dim && kpiLoading}
        />
        <StatCard
          icon="library_add_check"
          label="Matched entries"
          value={dim ? "0" : matched.toLocaleString()}
          dim={dim}
          loading={!dim && kpiLoading}
        />
        <StatCard
          icon="percent"
          label="Match rate"
          value={dim ? "0" : formatPercent(matchRate)}
          dim={dim}
          loading={!dim && kpiLoading}
        />
      </div>
    </div>
  );
}

const BADGE_LABELS: Record<ProcessingBadgeStatus, string> = {
  creating: "Creating entries…",
  processing: "Processing…",
  idle: "All entries processed",
};

function ProcessingStatusBadge({ status }: { status: ProcessingBadgeStatus }) {
  const active = status !== "idle";
  const label = BADGE_LABELS[status];
  return (
    <span
      role="status"
      className={[
        "flex items-center gap-1.5 rounded-full px-2 py-0.5 text-tiny font-medium",
        active ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "size-1.5 shrink-0 rounded-full",
          active ? "animate-pulse bg-yellow-500" : "bg-green-500",
        ].join(" ")}
      />
      {label}
    </span>
  );
}

type ToolbarProps = {
  query: string;
  onQueryChange?: (q: string) => void;
  searching?: boolean;
  disabled?: boolean;
  filter: EntryFilter;
  onFilterChange?: (f: EntryFilter) => void;
  selectMode: boolean;
  onToggleSelectMode?: () => void;
  selectedCount?: number;
  onDeleteSelected?: () => void;
  extra?: ReactNode;
  canManageEntries?: boolean;
};

export function Toolbar({
  query,
  onQueryChange,
  searching = false,
  disabled = false,
  filter,
  onFilterChange,
  selectMode,
  onToggleSelectMode,
  selectedCount = 0,
  onDeleteSelected,
  extra,
  canManageEntries = true,
}: ToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);

  return (
    <div className="flex flex-col gap-2 @xl:flex-row @xl:items-center">
      <label
        className={[
          "flex items-center gap-1.5 rounded-[4px] bg-grey-100 p-2 @xl:min-w-0 @xl:flex-1 @xl:basis-[240px]",
          disabled ? "opacity-40" : "",
        ].join(" ")}
      >
        {searching ? (
          <div className="size-5 shrink-0 animate-spin rounded-full border-2 border-grey-300 border-t-grey-600" />
        ) : (
          <Icon name="search" size={20} weight={300} className="text-grey-600" />
        )}
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange?.(e.target.value)}
          placeholder="Search"
          aria-label="Search monitoring entries"
          disabled={disabled}
          className="min-w-0 flex-1 border-0 bg-transparent text-small text-grey-900 outline-none placeholder:text-grey-600 disabled:cursor-not-allowed"
        />
      </label>
      <div className="flex items-center gap-2 @xl:contents">
        <div className="relative min-w-0 flex-1 @xl:w-[240px] @xl:flex-none">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            aria-haspopup="listbox"
            aria-expanded={filterOpen}
            className="flex w-full items-center gap-1 rounded-[4px] border border-grey-200 bg-white p-2 text-small font-medium text-grey-900 shadow-flat hover:bg-grey-50"
          >
            <span className="min-w-0 flex-1 truncate text-left">
              {ENTRY_FILTER_LABELS[filter]}
            </span>
            <Icon
              name="keyboard_arrow_down"
              size={20}
              weight={300}
              className={`transition-transform ${filterOpen ? "rotate-180" : ""}`}
            />
          </button>
          {filterOpen && (
            <>
              <div
                className="fixed inset-0 z-10"
                aria-hidden
                onClick={() => setFilterOpen(false)}
              />
              <div
                role="listbox"
                className="absolute left-0 right-0 top-full z-20 mt-1 flex flex-col rounded-[4px] border border-grey-200 bg-white p-1 shadow-flat"
              >
                {ENTRY_FILTERS.map((option) => {
                  const selected = option === filter;
                  return (
                    <button
                      key={option}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => {
                        onFilterChange?.(option);
                        setFilterOpen(false);
                      }}
                      className={[
                        "flex items-center gap-1 rounded-[4px] px-2 py-1.5 text-left text-small font-medium",
                        selected ? "bg-grey-100 text-black" : "text-black hover:bg-grey-50",
                      ].join(" ")}
                    >
                      <span className="flex-1">{ENTRY_FILTER_LABELS[option]}</span>
                      {selected && (
                        <Icon name="check_small" size={20} weight={300} className="text-black" />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 @xl:ml-auto">
          {extra}
          {canManageEntries && (
            <>
              {selectMode && (
                <button
                  type="button"
                  onClick={onDeleteSelected}
                  disabled={selectedCount === 0}
                  aria-label={
                    selectedCount > 0
                      ? `Delete ${selectedCount} selected ${selectedCount === 1 ? "entry" : "entries"}`
                      : "Delete selected entries"
                  }
                  className={[
                    "flex items-center gap-1 rounded-[4px] border border-grey-200 bg-white p-2 text-small font-medium shadow-flat",
                    selectedCount > 0
                      ? "text-red-600 hover:bg-grey-50"
                      : "cursor-not-allowed text-grey-300",
                  ].join(" ")}
                >
                  <Icon name="delete" size={20} weight={300} />
                  <span>{selectedCount > 0 ? `Delete (${selectedCount})` : "Delete"}</span>
                </button>
              )}
              <button
                type="button"
                onClick={onToggleSelectMode}
                disabled={disabled}
                aria-pressed={selectMode}
                className={[
                  "flex items-center gap-1 rounded-[4px] border p-2 text-small font-medium shadow-flat",
                  disabled
                    ? "cursor-not-allowed border-grey-200 bg-white text-grey-300"
                    : selectMode
                      ? "border-grey-500 bg-grey-100 text-black"
                      : "border-grey-200 bg-white text-black hover:bg-grey-50",
                ].join(" ")}
              >
                <Icon name="check_box" size={20} weight={300} />
                <span>Select</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
